import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { getCourseById, getLearnerById, getEnrolmentByCourseAndStaff } from '../api';
import logger from '../logger';
import './CertificateVerification.css';

// Import logos
import careLogo from '../assets/care.png';
import partnersStrip from '../assets/partners_strip.png';

const CertificateVerification = () => {
    const { courseId, userId } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState({
        learner: null,
        course: null,
        issueDate: null
    });

    useEffect(() => {
        const fetchCertificateData = async () => {
            try {
                setLoading(true);
                // Fetch learner and course data concurrently
                const [learnerData, courseData, enrolmentData] = await Promise.all([
                    getLearnerById(userId),
                    getCourseById(courseId),
                    getEnrolmentByCourseAndStaff(courseId, userId)
                ]);

                if (!learnerData) throw new Error('Learner not found');
                if (!courseData) throw new Error('Course not found');

                const parsedIssueDate = enrolmentData?.updated_at
                    ? new Date(enrolmentData.updated_at)
                    : null;

                setData({
                    learner: learnerData,
                    course: courseData,
                    issueDate: parsedIssueDate && !Number.isNaN(parsedIssueDate.getTime()) ? parsedIssueDate : null
                });
            } catch (err) {
                logger.error('Failed to verify certificate', err);
                setError('Invalid certificate or certificate not found. Please ensure you are logged in if this is a private verification.');
            } finally {
                setLoading(false);
            }
        };

        if (courseId && userId) {
            fetchCertificateData();
        }
    }, [courseId, userId]);

    if (loading) {
        return (
            <div className="certificate-loading">
                <div className="spinner"></div>
                <p>Verifying certificate...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="certificate-error">
                <div className="error-icon">❌</div>
                <h2>Verification Failed</h2>
                <p>{error}</p>
            </div>
        );
    }

    const { learner, course, issueDate } = data;
    const learnerName = `${learner.firstname || ''} ${learner.othernames || ''} ${learner.lastname || ''}`.trim();
    const courseName = course.title || 'Course Title';
    const certificateUrl = window.location.href;

    return (
        <div className="certificate-container">
            <div className="certificate-border">
                {/* Header Logos - Flex container */}
                <div className="certificate-header-logos">
                    {/* Independent CARE Logo */}
                    <img src={careLogo} alt="CARE" className="cert-logo care-logo" />

                    {/* Combined Strip for Katsina, SPHCA, Life Helpers */}
                    <img src={partnersStrip} alt="Partners: Katsina State, SPHCA, Life Helpers" className="cert-logo partners-strip" />
                </div>

                {/* Certificate Title */}
                <div className="certificate-title">
                    <h1>Certificate of Completion</h1>
                </div>

                {/* Content */}
                <div className="certificate-body">
                    <h2 className="recipient-name">{learnerName}</h2>
                    <p className="cert-text">has successfully completed the course</p>
                    <h3 className="course-name">{courseName}</h3>

                    <p className="provider-text">
                        This course was provided by: CARE International in partnership with the Katsina<br />
                        State Primary Healthcare Agency
                    </p>

                    <p className="date-issued">
                        Date Issued: {issueDate
                            ? issueDate.toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })
                            : 'N/A'}
                    </p>
                </div>

                {/* Footer: QR Code and Signatures */}
                <div className="certificate-footer">
                    <div className="qr-code-section">
                        <QRCodeCanvas value={certificateUrl} size={100} />
                    </div>

                    <div className="signatures-section">
                        <div className="signature-block">
                            <div className="signature-line"></div>
                            <p className="signatory-name">Hussaini Audu, PhD</p>
                            <p className="signatory-title">Country Director, CARE Nigeria</p>
                        </div>

                        <div className="signature-block">
                            <div className="signature-line"></div>
                            <p className="signatory-name">Dr Shamsuddeen Yahaya</p>
                            <p className="signatory-title">(MBBS, MHE, FWACP, FAIPH, FCIHE)</p>
                            <p className="signatory-title">Executive Secretary SPHCA Katsina</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CertificateVerification;
